/**
 * Picker for a reference attribute — choosing which entity, budget line or
 * employee a record points at.
 *
 * It searches on the server rather than filtering a preloaded list. The list
 * is capped at 500 options, and budget_entries already exceeds that: a
 * client-side filter over the first 500 would quietly make the remaining
 * budget lines unselectable, which is worse than slow.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { Loader2 } from 'lucide-react';

export interface Option { value: string; label: string }

interface Props {
  object: string;
  /** Shown immediately, so the current value is selectable before the fetch lands. */
  seed?: Option[];
  current: string | null;
  /**
   * Offer "Clear" — only where the column is nullable. Without it a reference
   * is write-once from the UI: the picker has no empty row to choose, so a
   * value set by mistake could never be taken back.
   */
  allowClear?: boolean;
  onPick: (value: string | null) => void;
  onCancel: () => void;
}

export function ReferencePicker({ object, seed, current, allowClear, onPick, onCancel }: Props) {
  const [term, setTerm] = useState('');
  const [options, setOptions] = useState<Option[]>(seed ?? []);
  const [loading, setLoading] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams({ lookup: '1' });
        if (term) p.set('q', term);
        const res = await fetch(`/api/fin/records/${object}?${p}`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('lookup failed');
        const body = await res.json();
        setOptions(body.options ?? []);
        setTruncated(Boolean(body.truncated));
        setFailed(false);
      } catch {
        // Keep whatever is already listed — a dropped request should not empty
        // a usable list — but say so, or an empty result reads as "no matches".
        setFailed(true);
      } finally {
        setLoading(false);
      }
    }, term ? 250 : 0);
    return () => clearTimeout(timer.current);
  }, [object, term]);

  return (
    // shouldFilter={false} — the server has already filtered; filtering again
    // would hide results whose match is in a column the label does not show.
    <Command shouldFilter={false} className="w-72">
      <CommandInput
        placeholder="Search…"
        value={term}
        onValueChange={setTerm}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
      />
      <CommandList className="max-h-64">
        {loading && (
          <div className="flex items-center gap-2 px-3 py-4 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" /> Searching…
          </div>
        )}
        {!loading && !failed && <CommandEmpty className="py-4 text-xs">No match.</CommandEmpty>}
        {failed && (
          <div className="px-3 py-4 text-xs text-muted-foreground">
            Could not load options. Type to try again.
          </div>
        )}
        {allowClear && current != null && current !== '' && (
          <CommandGroup>
            <CommandItem
              value="__clear__"
              onSelect={() => onPick(null)}
              className="text-xs text-muted-foreground"
            >
              Clear
            </CommandItem>
          </CommandGroup>
        )}
        <CommandGroup>
          {options.map((o) => (
            <CommandItem
              key={o.value}
              value={o.value}
              onSelect={() => onPick(o.value)}
              className={o.value === current ? 'text-xs font-medium' : 'text-xs'}
            >
              {o.label}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      {truncated && (
        <p className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
          Showing the first 500 — type to narrow.
        </p>
      )}
    </Command>
  );
}
